import React, { useState } from 'react';
import { AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { ImageSize, ImageAlignment } from '../../types/editor';

interface ImageMenuProps {
  onInsert: (size: ImageSize, alignment: ImageAlignment) => Promise<void>;
  onClose: () => void;
  position?: { x: number; y: number };
}

export const ImageMenu: React.FC<ImageMenuProps> = ({ onInsert, onClose, position }) => {
  const [size, setSize] = useState<ImageSize>('medium');
  const [alignment, setAlignment] = useState<ImageAlignment>('center');
  const [isInserting, setIsInserting] = useState(false);

  const handleInsert = async () => {
    try {
      setIsInserting(true);
      await onInsert(size, alignment);
      onClose();
    } catch (error) {
      console.error('Error inserting image:', error);
      alert('Erro ao inserir imagem. Por favor, tente novamente.');
    } finally {
      setIsInserting(false);
    }
  };

  const style = position ? {
    top: `${position.y}px`,
    left: `${position.x}px`
  } : {};

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-20" 
        onClick={onClose}
      />
      <div 
        className="image-menu"
        style={style}
        onClick={e => e.stopPropagation()}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tamanho
            </label>
            <select
              value={size}
              onChange={(e) => setSize(e.target.value as ImageSize)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="small">Pequeno (100x100)</option>
              <option value="medium">Médio (150x150)</option>
              <option value="large">Grande (200x200)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alinhamento
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setAlignment('left')}
                className={`flex-1 p-2 rounded ${
                  alignment === 'left' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-700'
                }`}
              >
                <AlignLeft className="h-5 w-5 mx-auto" />
              </button>
              <button
                type="button"
                onClick={() => setAlignment('center')}
                className={`flex-1 p-2 rounded ${
                  alignment === 'center' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-700'
                }`}
              >
                <AlignCenter className="h-5 w-5 mx-auto" />
              </button>
              <button
                type="button"
                onClick={() => setAlignment('right')}
                className={`flex-1 p-2 rounded ${
                  alignment === 'right' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-700'
                }`}
              >
                <AlignRight className="h-5 w-5 mx-auto" />
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              disabled={isInserting}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleInsert}
              disabled={isInserting}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
            >
              {isInserting ? 'Inserindo...' : 'Inserir'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};